import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppRoutes } from '@app/app.routes';
import { IInventoryItemWithCategory } from '@inventory/models/inventory';
import { IInventoryItemEditForm } from '@inventory/models/inventory-forms';
import { InventoryRoutes } from '@inventory/pages/inventory.routes';
import { InventoryItemsService } from '@inventory/services/inventory-items/inventory-items.service';
import { DropdownControlComponent, IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { SwitchControlComponent } from '@shared/components/forms/switch-control/switch-control.component';
import { TextareaControlComponent } from '@shared/components/forms/textarea-control/textarea-control.component';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-item-details-offcanvas',
	imports: [CommonModule, StandardControlComponent, TextareaControlComponent, DropdownControlComponent, SwitchControlComponent],
	templateUrl: './inventory-item-details-offcanvas.component.html',
	styleUrl: './inventory-item-details-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class InventoryItemDetailsOffcanvasComponent implements OnInit {
	@Input() categories: Array<IDropdownOption> = [];
	@Input() inventoryItem: IInventoryItemWithCategory;

	private inventoryItemsService = inject(InventoryItemsService);
	private offcanvasService = inject(OffcanvasService);
	private router = inject(Router);
	private toastService = inject(ToastService);

	form: FormGroup<IInventoryItemEditForm>;

	isSubmitting: boolean = false;

	ngOnInit(): void {
		this.form = new FormGroup<IInventoryItemEditForm>({
			name: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
			description: new FormControl<string | null>(null, [Validators.maxLength(500)]),
			sku: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
			category: new FormControl<string | null>(null),
			costPrice: new FormControl<number | null>(null, [Validators.min(0)]),
			sellPrice: new FormControl<number | null>(null, [Validators.min(0)]),
			archived: new FormControl<boolean | null>(false),
		});

		this.form.setValue({
			name: this.inventoryItem.name,
			description: this.inventoryItem.description,
			sku: this.inventoryItem.sku,
			category: this.inventoryItem.category?.id || null,
			costPrice: this.inventoryItem.costPrice,
			sellPrice: this.inventoryItem.sellPrice,
			archived: this.inventoryItem.archived,
		});

		if (this.categories.length < 1) {
			this.form.controls.category.disable();
		}
	}

	close(): void {
		this.offcanvasService.dismissLast();
	}

	navigateToCreateCategory(): void {
		this.offcanvasService.dismissLast();
		this.router.navigate([AppRoutes.INVENTORY, InventoryRoutes.CATEGORIES]);
	}

	submit(): void {
		if (this.form.invalid) {
			return;
		}

		this.isSubmitting = true;

		this.inventoryItemsService
			.updateInventoryItem(this.inventoryItem.id as string, this.form)
			.then(() => {
				this.isSubmitting = false;
				this.offcanvasService.dismissLast();
			})
			.catch((error) => {
				console.error('Error updating inventory item:', error);
				this.isSubmitting = false;
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Failed to update inventory item: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}
}
